/**
 * What deleting an account does with the member's credit, as the API's
 * deletion check states it before the admin confirms (Maude's request of
 * 22/10/2026). Until then the admin learnt it only from the deletion's own
 * response, once the account was gone.
 *
 * Read by hand rather than through the generated SDK type: the field is newer
 * than its last regeneration.
 */
export interface CreditOnDeletion {
  /** Cents going back to the member's cards. */
  toCard: number;
  /** Cents of bonus credit lost with the account. */
  forfeited: number;
  currency: string;
}

/**
 * Null when the API does not say, which is an API older than the field: the
 * dialog then says the amounts are unknown rather than showing zero, because a
 * zero would read as "nothing to settle".
 */
export function readCreditOnDeletion(eligibility: unknown): CreditOnDeletion | null {
  const credit = (eligibility as { creditOnDeletion?: Partial<CreditOnDeletion> } | null)?.creditOnDeletion;
  if (!credit || !Number.isFinite(credit.toCard) || !Number.isFinite(credit.forfeited)) {
    return null;
  }
  return {
    toCard: credit.toCard as number,
    forfeited: credit.forfeited as number,
    currency: credit.currency || 'EUR',
  };
}

export function hasCredit(credit: CreditOnDeletion | null): boolean {
  return !!credit && (credit.toCard > 0 || credit.forfeited > 0);
}

export function formatCents(cents: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}
